/**
 * useAIController Hook
 * Phase 7: AI制御の分離
 *
 * AI関連の状態管理とターン実行ロジックを統合
 * - aiAttackedMonsters: AIが攻撃済みのモンスター追跡
 * - aiActionCounter: AIアクション用カウンター（空振り時もuseEffect再トリガー用）
 * - AI選択処理（手札、モンスター、墓地、デッキ確認）
 * - AIメインフェイズ/バトルフェイズ実行
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type React from 'react';
import {
  createAIGameState,
  executeAIMainPhaseAction,
  executeAIBattlePhaseAction,
  handleAIHandSelection,
  handleAIMonsterTarget,
  handleAIGraveyardSelection,
  handleAIDeckReview,
  handleAIChainConfirmation,
  getStrategy,
} from '../../engine/ai';
import { AI_DELAY } from '../../engine/ai';
import { isSetsunaMagic } from '../../engine/keywordAbilities';
import { continuousEffectEngine } from '../../engine/continuousEffects';
import type { CardData, CardWithId, MonsterInstance, HandCard, FieldCardInstance, PhaseCardInstance } from '../../types/card';
import type { PlayerNumber, GameState, PlayerType, AIDifficulty } from '../../types/player';
import type {
  PendingHandSelection,
  PendingMonsterTarget,
  PendingGraveyardSelection,
  PendingDeckReview,
  ChainConfirmation,
} from '../../types/ui';

// 拡張されたPending型（selectionOwner付き）
interface PendingHandSelectionWithOwner extends PendingHandSelection {
  selectionOwner?: PlayerNumber;
}
interface PendingMonsterTargetWithOwner extends PendingMonsterTarget {
  selectionOwner?: PlayerNumber;
}
interface PendingGraveyardSelectionWithOwner extends PendingGraveyardSelection {
  selectionOwner?: PlayerNumber;
}
interface PendingDeckReviewWithOwner extends PendingDeckReview {
  selectionOwner?: PlayerNumber;
}

interface UseAIControllerDeps {
  // ゲーム状態
  gameState: GameState;
  phase: number;
  currentPlayer: PlayerNumber;
  turn: number;
  isFirstTurn: boolean;

  // プレイヤータイプ・難易度
  p1PlayerType: PlayerType;
  p2PlayerType: PlayerType;
  p1AIDifficulty: AIDifficulty;
  p2AIDifficulty: AIDifficulty;

  // ライフ
  p1Life: number;
  p2Life: number;

  // 手札
  p1Hand: HandCard[];
  p2Hand: HandCard[];

  // フィールド
  p1Field: (MonsterInstance | null)[];
  p2Field: (MonsterInstance | null)[];

  // 墓地
  p1Graveyard: CardWithId[];
  p2Graveyard: CardWithId[];

  // デッキ
  p1Deck: CardWithId[];
  p2Deck: CardWithId[];

  // SP
  p1ActiveSP: number;
  p2ActiveSP: number;
  p1RestedSP: number;
  p2RestedSP: number;
  p1BonusSP: number;
  p2BonusSP: number;

  // フィールドカード・フェイズカード
  p1FieldCard: FieldCardInstance | CardData | null;
  p2FieldCard: FieldCardInstance | CardData | null;
  p1PhaseCard: PhaseCardInstance | null;
  p2PhaseCard: PhaseCardInstance | null;

  // 選択状態
  pendingHandSelection: PendingHandSelectionWithOwner | null;
  setPendingHandSelection: React.Dispatch<React.SetStateAction<PendingHandSelectionWithOwner | null>>;
  pendingSelectedCard: HandCard | null;
  setPendingSelectedCard: React.Dispatch<React.SetStateAction<HandCard | null>>;
  pendingMonsterTarget: PendingMonsterTargetWithOwner | null;
  setPendingMonsterTarget: React.Dispatch<React.SetStateAction<PendingMonsterTargetWithOwner | null>>;
  pendingSelectedMonsterIndex: number | null;
  setPendingSelectedMonsterIndex: React.Dispatch<React.SetStateAction<number | null>>;
  pendingGraveyardSelection: PendingGraveyardSelectionWithOwner | null;
  setPendingGraveyardSelection: React.Dispatch<React.SetStateAction<PendingGraveyardSelectionWithOwner | null>>;
  pendingGraveyardSelectedCard: CardWithId | null;
  setPendingGraveyardSelectedCard: React.Dispatch<React.SetStateAction<CardWithId | null>>;
  pendingDeckReview: PendingDeckReviewWithOwner | null;
  setPendingDeckReview: React.Dispatch<React.SetStateAction<PendingDeckReviewWithOwner | null>>;

  // チェーン確認
  chainConfirmation: ChainConfirmation | null;

  // バトルアクション
  summonCard: (card: HandCard, slotIndex: number) => boolean;
  chargeCard: (card: HandCard, monsterIndex: number) => boolean;
  attack: (attackerIndex: number, targetIndex: number | null) => boolean;
  executeSkill: (monsterIndex: number, skillType: 'basic' | 'advanced') => boolean;
  nextPhase: () => void;

  // 刹那詠唱
  handleSkipChainConfirmation: () => void;
  activateSetsunaInChain: (card: HandCard) => void;

  // チャージ使用フラグ
  chargeUsedThisTurn: boolean;

  // ログ
  addLog: (message: string, type?: 'info' | 'damage' | 'heal') => void;

  // AI思考速度取得関数
  getScaledDelay: (baseDelay: number) => number;
}

interface UseAIControllerReturn {
  aiAttackedMonsters: Set<number>;
  aiActionCounter: number;
  resetAI: () => void;
}

/**
 * useAIController Hook
 */
export const useAIController = (deps: UseAIControllerDeps): UseAIControllerReturn => {
  const {
    // ゲーム状態
    gameState,
    phase,
    currentPlayer,
    turn,
    isFirstTurn,

    // プレイヤータイプ・難易度
    p1PlayerType,
    p2PlayerType,
    p1AIDifficulty,
    p2AIDifficulty,

    // ライフ
    p1Life,
    p2Life,

    // 手札
    p1Hand,
    p2Hand,

    // フィールド
    p1Field,
    p2Field,

    // 墓地
    p1Graveyard,
    p2Graveyard,

    // デッキ
    p1Deck,
    p2Deck,

    // SP
    p1ActiveSP,
    p2ActiveSP,
    p1RestedSP,
    p2RestedSP,
    p1BonusSP,
    p2BonusSP,

    // フィールドカード・フェイズカード
    p1FieldCard,
    p2FieldCard,
    p1PhaseCard,
    p2PhaseCard,

    // 選択状態
    pendingHandSelection,
    setPendingHandSelection,
    pendingSelectedCard,
    setPendingSelectedCard,
    pendingMonsterTarget,
    setPendingMonsterTarget,
    pendingSelectedMonsterIndex,
    setPendingSelectedMonsterIndex,
    pendingGraveyardSelection,
    setPendingGraveyardSelection,
    pendingGraveyardSelectedCard,
    setPendingGraveyardSelectedCard,
    pendingDeckReview,
    setPendingDeckReview,

    // チェーン確認
    chainConfirmation,

    // バトルアクション
    summonCard,
    chargeCard,
    attack,
    executeSkill,
    nextPhase,

    // 刹那詠唱
    handleSkipChainConfirmation,
    activateSetsunaInChain,

    // チャージ使用フラグ
    chargeUsedThisTurn,

    // ログ
    addLog,

    // AI思考速度取得関数
    getScaledDelay,
  } = deps;

  // =============================================================================
  // AI専用State
  // =============================================================================
  const [aiAttackedMonsters, setAiAttackedMonsters] = useState<Set<number>>(new Set());
  const [aiActionCounter, setAiActionCounter] = useState<number>(0);

  // フェイズ変更追跡用ref
  const prevPhaseRef = useRef<number>(phase);

  // =============================================================================
  // リセット関数
  // =============================================================================
  const resetAI = useCallback((): void => {
    setAiAttackedMonsters(new Set());
    setAiActionCounter(0);
  }, []);

  // =============================================================================
  // AIプレイヤー判定ヘルパー
  // =============================================================================
  const isPlayerAI = useCallback((player: PlayerNumber): boolean =>
    (player === 1 && p1PlayerType === 'ai') ||
    (player === 2 && p2PlayerType === 'ai'),
  [p1PlayerType, p2PlayerType]);

  // =============================================================================
  // バトルフェイズ開始時にAI攻撃済みモンスターをリセット
  // =============================================================================
  useEffect(() => {
    // フェイズが3（バトルフェイズ）に変わったときのみリセット
    if (phase === 3 && prevPhaseRef.current !== 3) {
      setAiAttackedMonsters(new Set());
    }
    prevPhaseRef.current = phase;
  }, [phase]);

  // =============================================================================
  // AIターン実行
  // =============================================================================
  useEffect(() => {
    if (gameState !== 'playing') return;

    // 特殊ケース: チェーン確認（相手ターンでも発生するため最優先でチェック）
    if (chainConfirmation) {
      const askingPlayer = chainConfirmation.askingPlayer;
      if (isPlayerAI(askingPlayer)) {
        const difficulty = askingPlayer === 1 ? p1AIDifficulty : p2AIDifficulty;
        const strategy = getStrategy(difficulty);

        const gameStateData = {
          phase, turn, isFirstTurn,
          p1Life, p2Life,
          p1Hand, p2Hand,
          p1Field, p2Field,
          p1Graveyard, p2Graveyard,
          p1Deck, p2Deck,
          p1ActiveSP, p2ActiveSP,
          p1RestedSP, p2RestedSP,
          p1BonusSP, p2BonusSP,
          p1FieldCard, p2FieldCard,
          p1PhaseCard, p2PhaseCard,
        };
        const aiGameState = createAIGameState(askingPlayer, gameStateData);

        // 継続効果を考慮した発動可能カードの取得
        const aiHand = askingPlayer === 1 ? p1Hand : p2Hand;
        const aiActiveSP = askingPlayer === 1 ? p1ActiveSP : p2ActiveSP;
        const aiMagicCostContext = {
          effectOwner: askingPlayer,
          currentPlayer: askingPlayer,
          p1Field,
          p2Field,
          p1Life,
          p2Life,
        };
        const availableSetsunaCards = aiHand.filter(card => {
          if (!isSetsunaMagic(card)) return false;
          const baseCost = card.cost || 0;
          // TargetCard型はindex signatureを持つため、unknownを経由してキャスト
          const costModifier = continuousEffectEngine.calculateMagicCostModifier(
            card as unknown as { uniqueId?: string; owner?: PlayerNumber; [key: string]: unknown },
            askingPlayer,
            aiMagicCostContext
          );
          const actualCost = Math.max(0, baseCost + costModifier);
          return aiActiveSP >= actualCost;
        });

        const timeoutId = setTimeout(() => {
          // ChainConfirmation型の互換性のためunknownを経由してキャスト
          handleAIChainConfirmation(
            chainConfirmation as unknown as Parameters<typeof handleAIChainConfirmation>[0],
            availableSetsunaCards,
            aiGameState,
            strategy,
            { skipChainConfirmation: handleSkipChainConfirmation, activateSetsunaInChain }
          );
        }, getScaledDelay(AI_DELAY.LONG));
        return () => clearTimeout(timeoutId);
      }
      return; // 人間プレイヤーがチェーン確認中
    }

    // 通常ターン: 現在のプレイヤーがAIか判定
    if (!isPlayerAI(currentPlayer)) return;

    const difficulty = currentPlayer === 1 ? p1AIDifficulty : p2AIDifficulty;
    const strategy = getStrategy(difficulty);

    const gameStateData = {
      phase, turn, isFirstTurn,
      p1Life, p2Life,
      p1Hand, p2Hand,
      p1Field, p2Field,
      p1Graveyard, p2Graveyard,
      p1Deck, p2Deck,
      p1ActiveSP, p2ActiveSP,
      p1RestedSP, p2RestedSP,
      p1BonusSP, p2BonusSP,
      p1FieldCard, p2FieldCard,
      p1PhaseCard, p2PhaseCard,
    };
    const aiGameState = createAIGameState(currentPlayer, gameStateData);

    // 特殊ケース: 手札選択
    // selectionOwnerが設定されている場合、そのプレイヤーがAIでなければスキップ
    // （刹那詠唱で人間が発動した場合、currentPlayerはAIでもselectionOwnerは人間）
    if (pendingHandSelection) {
      const selectionOwner = pendingHandSelection.selectionOwner ?? currentPlayer;
      if (!isPlayerAI(selectionOwner)) return; // 選択は人間プレイヤーのもの
      const timeoutId = setTimeout(() => {
        // AI用型との互換性のためキャスト
        handleAIHandSelection(
          pendingHandSelection as unknown as Parameters<typeof handleAIHandSelection>[0],
          aiGameState,
          strategy
        );
        setPendingHandSelection(null);
        setPendingSelectedCard(null);
      }, getScaledDelay(AI_DELAY.MEDIUM));
      return () => clearTimeout(timeoutId);
    }

    // 特殊ケース: モンスターターゲット選択
    if (pendingMonsterTarget) {
      const selectionOwner = pendingMonsterTarget.selectionOwner ?? currentPlayer;
      if (!isPlayerAI(selectionOwner)) return; // 選択は人間プレイヤーのもの
      const timeoutId = setTimeout(() => {
        // AI用型との互換性のためキャスト
        handleAIMonsterTarget(
          pendingMonsterTarget as unknown as Parameters<typeof handleAIMonsterTarget>[0],
          aiGameState,
          strategy
        );
        setPendingMonsterTarget(null);
        setPendingSelectedMonsterIndex(null);
      }, getScaledDelay(AI_DELAY.MEDIUM));
      return () => clearTimeout(timeoutId);
    }

    // 特殊ケース: 墓地選択
    if (pendingGraveyardSelection) {
      const selectionOwner = pendingGraveyardSelection.selectionOwner ?? currentPlayer;
      if (!isPlayerAI(selectionOwner)) return; // 選択は人間プレイヤーのもの
      const timeoutId = setTimeout(() => {
        // AI用型との互換性のためキャスト
        handleAIGraveyardSelection(
          pendingGraveyardSelection as unknown as Parameters<typeof handleAIGraveyardSelection>[0],
          aiGameState,
          strategy
        );
        setPendingGraveyardSelection(null);
        setPendingGraveyardSelectedCard(null);
      }, getScaledDelay(AI_DELAY.MEDIUM));
      return () => clearTimeout(timeoutId);
    }

    // 特殊ケース: デッキ確認
    if (pendingDeckReview) {
      const selectionOwner = pendingDeckReview.selectionOwner ?? currentPlayer;
      if (!isPlayerAI(selectionOwner)) return; // 選択は人間プレイヤーのもの
      const timeoutId = setTimeout(() => {
        // AI用型との互換性のためキャスト
        handleAIDeckReview(
          pendingDeckReview as unknown as Parameters<typeof handleAIDeckReview>[0],
          aiGameState,
          strategy
        );
        setPendingDeckReview(null);
      }, getScaledDelay(AI_DELAY.MEDIUM));
      return () => clearTimeout(timeoutId);
    }

    // メインフェイズ
    if (phase === 2) {
      const timeoutId = setTimeout(() => {
        const actions = {
          summonCard: (card: HandCard, slot: number) => summonCard(card, slot),
          executeSkill: (monsterIndex: number, skillType: 'basic' | 'advanced') => executeSkill(monsterIndex, skillType),
          activateMagicCard: (card: HandCard) => {
            // 魔法カードの発動処理を直接実行
            if (card && card.type === 'magic') {
              summonCard(card, 0);
            }
          },
          activateTrigger: (_trigger: unknown) => {
            // トリガー発動ロジック（簡易実装）
            addLog(`AI: トリガー発動`, 'info');
          },
          chargeCard: (card: HandCard, monsterIndex: number) => chargeCard(card, monsterIndex),
          placeFieldCard: (card: HandCard) => {
            // フィールドカードの配置（summonCardがfieldタイプを処理）
            if (card && card.type === 'field') {
              summonCard(card, 0);
            }
          },
          placePhaseCard: (card: HandCard) => {
            // フェイズカードの配置（summonCardがphaseタイプを処理）
            if (card && card.type === 'phasecard') {
              summonCard(card, 0);
            }
          },
          nextPhase: () => nextPhase(),
        };

        executeAIMainPhaseAction(aiGameState, actions, strategy, {
          chargeUsedThisTurn,
        });
        // アクション実行後にカウンターをインクリメント（空振り時もuseEffect再トリガー用）
        setAiActionCounter(prev => prev + 1);
      }, getScaledDelay(AI_DELAY.MEDIUM));
      return () => clearTimeout(timeoutId);
    }

    // バトルフェイズ
    if (phase === 3) {
      const timeoutId = setTimeout(() => {
        const actions = {
          // AI用AttackTargetをUI用targetIndexに変換（'direct' -> null）
          attack: (attackerIndex: number, target: number | 'direct') => {
            const targetIndex = target === 'direct' ? null : target;
            attack(attackerIndex, targetIndex);
          },
          nextPhase: () => nextPhase(),
        };

        const result = executeAIBattlePhaseAction(aiGameState, actions, strategy, aiAttackedMonsters);
        setAiAttackedMonsters(result.attackedMonsters);
        // アクション実行後にカウンターをインクリメント（空振り時もuseEffect再トリガー用）
        setAiActionCounter(prev => prev + 1);
      }, getScaledDelay(AI_DELAY.MEDIUM));
      return () => clearTimeout(timeoutId);
    }
  }, [
    gameState, phase, currentPlayer, turn, isFirstTurn, isPlayerAI,
    p1AIDifficulty, p2AIDifficulty,
    p1Life, p2Life, p1Hand, p2Hand, p1Field, p2Field,
    p1Graveyard, p2Graveyard, p1Deck, p2Deck,
    p1ActiveSP, p2ActiveSP, p1RestedSP, p2RestedSP, p1BonusSP, p2BonusSP,
    p1FieldCard, p2FieldCard, p1PhaseCard, p2PhaseCard,
    pendingHandSelection, setPendingHandSelection, setPendingSelectedCard,
    pendingMonsterTarget, setPendingMonsterTarget, setPendingSelectedMonsterIndex,
    pendingGraveyardSelection, setPendingGraveyardSelection, setPendingGraveyardSelectedCard,
    pendingDeckReview, setPendingDeckReview,
    chainConfirmation, aiAttackedMonsters, chargeUsedThisTurn,
    aiActionCounter,
    summonCard, chargeCard, attack, executeSkill, nextPhase,
    handleSkipChainConfirmation, activateSetsunaInChain,
    addLog, getScaledDelay,
  ]);

  return {
    // State（読み取り専用）
    aiAttackedMonsters,
    aiActionCounter,

    // リセット関数
    resetAI,
  };
};

export default useAIController;
